'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

export default function LandingNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sellerMenuOpen, setSellerMenuOpen] = useState(false);
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false);
  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  const sellerMenuRef = useRef<HTMLDivElement>(null);
  const customerMenuRef = useRef<HTMLDivElement>(null);
  const agentMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (sellerMenuRef.current && !sellerMenuRef.current.contains(e.target as Node)) {
        setSellerMenuOpen(false);
      }
      if (customerMenuRef.current && !customerMenuRef.current.contains(e.target as Node)) {
        setCustomerMenuOpen(false);
      }
      if (agentMenuRef.current && !agentMenuRef.current.contains(e.target as Node)) {
        setAgentMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Benefits', href: '#benefits' },
    { label: 'About Us', href: '/about' },
  ];

  const sellerLinks = [
    { label: 'Become a Seller', href: '/seller-sign-up', icon: 'RocketLaunchIcon', desc: 'Start selling on Bozaat' },
    { label: 'Seller Login', href: '/seller-login', icon: 'ArrowRightOnRectangleIcon', desc: 'Access your dashboard' },
    { label: 'Seller Guidelines', href: '/seller-guidelines', icon: 'BookOpenIcon', desc: 'Policies & best practices' },
    { label: 'Seller Support', href: '/seller-support', icon: 'LifebuoyIcon', desc: 'Get help from our team' },
  ];

  const customerLinks = [
    { label: 'Create Account', href: '/customer-sign-up', icon: 'UserPlusIcon', desc: 'Shop eco-friendly products' },
    { label: 'Customer Login', href: '/customer-login', icon: 'ArrowRightOnRectangleIcon', desc: 'Access your account' },
  ];

  const agentLinks = [
    { label: 'Become a Delivery Agent', href: '/agent-sign-up', icon: 'TruckIcon', desc: 'Join our delivery network' },
    { label: 'Agent Login', href: '/agent-login', icon: 'ArrowRightOnRectangleIcon', desc: 'Access your agent dashboard' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 group">
            <AppLogo size={36} />
            <span
              className={`font-extrabold text-xl tracking-tight transition-colors ${
                scrolled ? 'text-foreground' : 'text-white'
              }`}
            >
              Bozaat
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks?.map((link) => (
              <a
                key={`nav-${link?.label}`}
                href={link?.href}
                className={`text-sm font-500 transition-colors hover:text-primary ${
                  scrolled ? 'text-secondary-foreground' : 'text-white/80'
                }`}
              >
                {link?.label}
              </a>
            ))}

            {/* Sellers Dropdown */}
            <div className="relative" ref={sellerMenuRef}>
              <button
                onClick={() => setSellerMenuOpen(!sellerMenuOpen)}
                className={`flex items-center gap-1 text-sm font-500 transition-colors hover:text-primary ${
                  scrolled ? 'text-secondary-foreground' : 'text-white/80'
                }`}
              >
                Sellers
                <Icon
                  name={sellerMenuOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={14}
                  className="mt-0.5"
                />
              </button>
              {sellerMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-border rounded-2xl shadow-lg overflow-hidden z-50">
                  {sellerLinks.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setSellerMenuOpen(false)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors group"
                    >
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name={item.icon as never} size={15} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-600 text-foreground group-hover:text-primary transition-colors">
                          {item.label}
                        </p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Customers Dropdown */}
            <div className="relative" ref={customerMenuRef}>
              <button
                onClick={() => setCustomerMenuOpen(!customerMenuOpen)}
                className={`flex items-center gap-1 text-sm font-500 transition-colors hover:text-primary ${
                  scrolled ? 'text-secondary-foreground' : 'text-white/80'
                }`}
              >
                Customers
                <Icon
                  name={customerMenuOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={14}
                  className="mt-0.5"
                />
              </button>
              {customerMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-border rounded-2xl shadow-lg overflow-hidden z-50">
                  {customerLinks.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setCustomerMenuOpen(false)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors group"
                    >
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name={item.icon as never} size={15} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-600 text-foreground group-hover:text-primary transition-colors">
                          {item.label}
                        </p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Delivery Agents Dropdown */}
            <div className="relative" ref={agentMenuRef}>
              <button
                onClick={() => setAgentMenuOpen(!agentMenuOpen)}
                className={`flex items-center gap-1 text-sm font-500 transition-colors hover:text-primary ${
                  scrolled ? 'text-secondary-foreground' : 'text-white/80'
                }`}
              >
                Agents
                <Icon
                  name={agentMenuOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={14}
                  className="mt-0.5"
                />
              </button>
              {agentMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-60 bg-white border border-border rounded-2xl shadow-lg overflow-hidden z-50">
                  {agentLinks.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setAgentMenuOpen(false)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors group"
                    >
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name={item.icon as never} size={15} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-600 text-foreground group-hover:text-primary transition-colors">
                          {item.label}
                        </p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              href="/shop"
              className={`text-sm font-600 px-4 py-2 rounded-lg transition-colors ${
                scrolled
                  ? 'text-secondary-foreground hover:text-primary'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Browse Shop
            </Link>
            <Link
              href="/customer-login"
              className={`text-sm font-600 px-4 py-2 rounded-lg transition-colors ${
                scrolled
                  ? 'text-secondary-foreground hover:text-primary'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Sign In
            </Link>
            <Link
              href="/customer-sign-up"
              className="btn-primary text-sm font-600 px-5 py-2 rounded-lg"
            >
              Shop Now
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'text-foreground' : 'text-white'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle mobile menu"
          >
            <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-border shadow-lg">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks?.map((link) => (
              <a
                key={`mobile-nav-${link?.label}`}
                href={link?.href}
                className="text-sm font-500 text-secondary-foreground hover:text-primary transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {link?.label}
              </a>
            ))}

            {/* Seller links in mobile */}
            <div className="border-t border-border pt-3">
              <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-3">Sellers</p>
              <div className="flex flex-col gap-2">
                {sellerLinks.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2 text-sm font-500 text-secondary-foreground hover:text-primary transition-colors"
                  >
                    <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon as never} size={14} className="text-primary" />
                    </div>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Customer links in mobile */}
            <div className="border-t border-border pt-3">
              <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-3">Customers</p>
              <div className="flex flex-col gap-2 mb-2">
                {customerLinks.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2 text-sm font-500 text-secondary-foreground hover:text-primary transition-colors"
                  >
                    <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon as never} size={14} className="text-primary" />
                    </div>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Delivery Agent links in mobile */}
            <div className="border-t border-border pt-3">
              <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-3">Delivery Agents</p>
              <div className="flex flex-col gap-2 mb-2">
                {agentLinks.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2 text-sm font-500 text-secondary-foreground hover:text-primary transition-colors"
                  >
                    <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon as never} size={14} className="text-primary" />
                    </div>
                    {item.label}
                  </Link>
                ))}
              </div>
              <Link
                href="/agent-login"
                className="text-sm font-600 text-center py-2 rounded-lg border border-border text-secondary-foreground hover:bg-muted transition-colors block"
                onClick={() => setMobileOpen(false)}
              >
                Agent Login
              </Link>
              <Link
                href="/agent-sign-up"
                className="btn-primary text-sm font-600 text-center py-2.5 rounded-lg block mt-2"
                onClick={() => setMobileOpen(false)}
              >
                Become a Delivery Agent
              </Link>
            </div>

            <div className="border-t border-border pt-4 flex flex-col gap-3">
              <Link
                href="/seller-login"
                className="text-sm font-600 text-center py-2 rounded-lg border border-border text-secondary-foreground hover:bg-muted transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Seller Login
              </Link>
              <Link
                href="/seller-sign-up"
                className="btn-primary text-sm font-600 text-center py-2.5 rounded-lg"
                onClick={() => setMobileOpen(false)}
              >
                Become a Seller
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}